import { makeStyles } from 'tss-react/mui';
import { FC, useCallback, useMemo } from 'react';
import { SortDirection } from 'react-data-grid';
import { AsyncLoadingState } from 'async-utils';
import { important, Nullable } from 'common-types';
import { useInfinityScroll } from 'react-utils';
import OxDataGrid from '../../../elements/OxDataGrid/OxDataGrid';
import { loadApplications } from '../../actions/applications-actions';
import { sortApplications } from '../../actions/applications-sort-actions';
import { loadIssuesScore } from '../../actions/single-application-actions';
import {
  ApplicationRow,
  ApplicationsStoreCategories,
} from '../../applications-types';
import config from '../../config/applications-config.json';
import { useApplicationHeader } from '../../hooks/use-application-header';
import { selectApplication } from '../../stores/applications-store-actions';

const ApplicationsTable: FC<ApplicationsTableProps> = ({
  applications,
  categories,
  selectedAppId,
  loading,
  loadingFilters,
  orderDirection,
  orderField,
  orderByCategory,
}) => {
  const { classes } = useStyles();

  const { onScroll } = useInfinityScroll({
    load: loadApplications,
    threshold: 0.9,
  });

  const onRowClick = useCallback((row: ApplicationRow) => {
    selectApplication(row.appId);
    loadIssuesScore(row.appName);
  }, []);

  const newColumns = useApplicationHeader(categories, classes);

  const showLoader = loading.isPending() || loadingFilters.isPending();

  const sortColumnsInfo = useMemo(() => {
    return orderField && orderField
      ? [
          {
            columnKey: orderByCategory || orderField,
            direction: orderDirection,
          },
        ]
      : undefined;
  }, [orderField, orderByCategory, orderDirection]);

  return (
    <div className={classes.tableContainer}>
      <OxDataGrid
        onRowClick={onRowClick}
        activeRowKey='appId'
        activeRowValue={selectedAppId}
        columns={newColumns}
        rows={applications}
        rowHeight={config.table.rowHeight}
        onScroll={onScroll}
        defaultColumnOptions={{ sortable: true, resizable: false }}
        sortColumns={sortColumnsInfo}
        onSort={sortApplications}
        loading={showLoader}
      />
    </div>
  );
};

const useStyles = makeStyles()({
  tableContainer: {
    borderRadius: 10,
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  textCenter: {
    textAlign: important('center'),
  },
  flexCenter: {
    justifyContent: 'center',
  },
  headerCell: {
    textAlign: 'center',
    '& .rdg-header-sort-name': {
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      lineHeight: 1,
      alignSelf: 'center',
      fontSize: 10,
    },
  },
  noPadding: {
    paddingInline: important('0'),
  },
  justifyStart: {
    justifyContent: 'flex-start',
  },
  textAlignStart: {
    textAlign: 'start',
  },
});

export interface ApplicationsTableProps {
  applications: ApplicationRow[];
  categories: ApplicationsStoreCategories;
  selectedAppId: Nullable<string>;
  loading: AsyncLoadingState;
  loadingFilters: AsyncLoadingState;
  loadingSingleApp: boolean;
  orderField?: string;
  orderDirection: SortDirection;
  orderByCategory?: string;
}

export default ApplicationsTable;
